import React, { useCallback, useMemo, useState } from 'react'
import { CBadge, CButton, CCard, CCardBody } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilInfo } from '@coreui/icons'

import UserService from 'src/service/UserService'
import MctGameTable from './MctGameTable'
import MctGameDetailPanel from './MctGameDetailPanel'
import {
  GameTypeBadge,
  PlanBadge,
  YesNoBadge,
  collectionMethodLabel,
  gameModeLabel,
  gamePassLabel,
} from './mctGameHelpers'
import './User.css'
import './MctGames.css'

const num = (value) => <td className="text-end pe-4">{value ?? 0}</td>
const numCol = (label, key) => ({ label, key, filter: false, _style: { textAlign: 'right' } })

// Each tab is one listing endpoint with its own columns; the game/hub identity columns and
// search live in MctGameTable.
const buildSections = (openGame) => [
  {
    key: 'all',
    label: 'All Games',
    note: 'Every game across all hubs, with its size, type and plan.',
    fetcher: UserService.getMctGameListing,
    noItemsLabel: 'No games found',
    columns: [
      numCol('Members', 'member_count'),
      numCol('Teams', 'team_count'),
      numCol('Players', 'player_count'),
      { label: 'Game Type', key: 'game_type', filter: false },
      { label: 'Pricing Plan', key: 'pricing_plan', filter: false },
      { label: '', key: 'action', filter: false, sorter: false, _style: { width: '1%' } },
    ],
    scopedColumns: {
      member_count: (item) => num(item.member_count),
      team_count: (item) => num(item.team_count),
      player_count: (item) => num(item.player_count),
      game_type: (item) => (
        <td>
          <GameTypeBadge type={item.game_type} />
        </td>
      ),
      pricing_plan: (item) => (
        <td>
          <PlanBadge plan={item.pricing_plan} />
        </td>
      ),
      action: (item) => (
        <td>
          <CButton
            color="primary"
            variant="outline"
            size="sm"
            className="ge-row-icon-btn"
            title="View game details"
            aria-label="View game details"
            onClick={() => openGame(item)}
          >
            <CIcon icon={cilInfo} />
          </CButton>
        </td>
      ),
    },
  },
  {
    key: 'structure',
    label: 'Game Structure',
    note: 'Team size, game mode, salary cap and trades for each game.',
    fetcher: UserService.getMctGameAccord2Listing,
    noItemsLabel: 'No games found',
    columns: [
      numCol('Team Size', 'team_size'),
      { label: 'Mode', key: 'team_view', filter: false },
      numCol('Salary Cap', 'salary_cap'),
      numCol('Trades Allowed', 'trades'),
      numCol('Trades Used', 'trade_used'),
    ],
    scopedColumns: {
      team_size: (item) => num(item.team_size),
      team_view: (item) => (
        <td>
          <CBadge color="primary" shape="rounded-pill" className="px-2 py-1">
            {gameModeLabel(item.team_view)}
          </CBadge>
        </td>
      ),
      salary_cap: (item) => <td className="text-end pe-4">${item.salary_cap ?? 0}</td>,
      trades: (item) => num(item.trades),
      trade_used: (item) => num(item.trade_used),
    },
  },
  {
    key: 'privacy',
    label: 'Privacy & Limits',
    note: 'Who can see each game, squad alerts and player selection limits.',
    fetcher: UserService.getMctGameAccord4Listing,
    noItemsLabel: 'No games found',
    columns: [
      { label: 'Game Privacy', key: 'game_privacy', filter: false },
      { label: 'Squad Alert', key: 'player_alert', filter: false },
      numCol('Player Selection Limit', 'team_selection_limit'),
    ],
    scopedColumns: {
      game_privacy: (item) => (
        <td>
          <YesNoBadge value={item.game_privacy === 1} yes="Public" no="Private" />
        </td>
      ),
      player_alert: (item) => (
        <td>
          <YesNoBadge value={item.player_alert === 1} />
        </td>
      ),
      team_selection_limit: (item) => num(item.team_selection_limit),
    },
  },
  {
    key: 'fixtures',
    label: 'Fixtures',
    note: 'How far through its fixtures each game is.',
    fetcher: UserService.getMctGameAccord3Listing,
    noItemsLabel: 'No games found',
    columns: [
      numCol('Total', 'totalFixture'),
      numCol('Not Started', 'notStartedFixture'),
      numCol('In Progress', 'inProgressFixture'),
      numCol('Completed', 'completedFixture'),
    ],
    scopedColumns: {
      totalFixture: (item) => (
        <td className="text-end pe-4 fw-semibold">{item.totalFixture ?? 0}</td>
      ),
      notStartedFixture: (item) => num(item.notStartedFixture),
      inProgressFixture: (item) => num(item.inProgressFixture),
      completedFixture: (item) => num(item.completedFixture),
    },
  },
  {
    key: 'addons',
    label: 'PRO Add-Ons',
    note: 'Which PRO add-ons each game has switched on.',
    fetcher: UserService.getMctGameAccord5Listing,
    noItemsLabel: 'No games found',
    columns: [
      { label: 'Bonus Cards', key: 'is_bonus_card_activate', filter: false },
      { label: 'Team of Round', key: 'is_totr_activate', filter: false },
      { label: 'Bracket Battle', key: 'is_bracket_battle_activate', filter: false },
      { label: 'Head-2-Head', key: 'is_h2h_activate', filter: false },
      { label: 'Votes', key: 'is_voting_activate', filter: false },
    ],
    scopedColumns: Object.fromEntries(
      [
        'is_bonus_card_activate',
        'is_totr_activate',
        'is_bracket_battle_activate',
        'is_h2h_activate',
        'is_voting_activate',
      ].map((key) => [
        key,
        (item) => (
          <td>
            <YesNoBadge value={item[key] === 1} />
          </td>
        ),
      ]),
    ),
  },
  {
    key: 'payments',
    label: 'Payments',
    note: 'How each game collects its game pass. Open a game for its earnings.',
    // Same endpoint as Privacy & Limits - it also returns the game-pass fields.
    fetcher: UserService.getMctGameAccord4Listing,
    noItemsLabel: 'No games found',
    columns: [
      { label: 'Game Pass', key: 'collect_game_pass', filter: false },
      { label: 'Collection Method', key: 'collect_game_pass_method', filter: false, sorter: false },
      {
        label: 'Collection Required',
        key: 'collect_game_pass_required',
        filter: false,
        sorter: false,
      },
    ],
    scopedColumns: {
      collect_game_pass: (item) => (
        <td>
          <CBadge
            color={item.collect_game_pass == 0 ? 'secondary' : 'info'}
            shape="rounded-pill"
            className="px-2 py-1"
          >
            {gamePassLabel(item.collect_game_pass)}
          </CBadge>
        </td>
      ),
      collect_game_pass_method: (item) => (
        <td>{collectionMethodLabel(item.collect_game_pass) || '-'}</td>
      ),
      collect_game_pass_required: (item) => (
        <td>
          <YesNoBadge value={item.collect_game_pass == 1} />
        </td>
      ),
    },
  },
]

const MctGames = () => {
  const [activeTab, setActiveTab] = useState('all')
  // A section is mounted the first time its tab is opened and then kept, so switching back
  // keeps its page and search.
  const [visited, setVisited] = useState({ all: true })
  const [totals, setTotals] = useState({})
  const [openGame, setOpenGame] = useState(null)

  const sections = useMemo(() => buildSections(setOpenGame), [])

  // Stable per-section reporters - MctGameTable keeps them in its fetch dependencies.
  const totalReporters = useMemo(
    () =>
      Object.fromEntries(
        sections.map((s) => [
          s.key,
          (total) =>
            setTotals((current) =>
              current[s.key] === total ? current : { ...current, [s.key]: total },
            ),
        ]),
      ),
    [sections],
  )

  const selectTab = useCallback((key) => {
    setActiveTab(key)
    setVisited((current) => (current[key] ? current : { ...current, [key]: true }))
  }, [])

  const active = sections.find((s) => s.key === activeTab)

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">MCT Games</h1>
        <p className="ge-page-subtitle">
          Every game on the platform. Click a game name to see its structure, fixtures, add-ons and
          payments.
        </p>
      </div>

      <div className="ge-tabs" role="tablist">
        {sections.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`ge-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => selectTab(tab.key)}
          >
            {tab.label}
            {totals[tab.key] !== undefined && (
              <span className="ge-tab-count">{totals[tab.key]}</span>
            )}
          </button>
        ))}
      </div>

      <CCard className="um-panel">
        <CCardBody>
          <div className="um-panel-heading">
            <div>
              <h2 className="um-panel-title">{active?.label}</h2>
              <p className="um-panel-note">{active?.note}</p>
            </div>
          </div>

          {sections.map((section) =>
            visited[section.key] ? (
              <div key={section.key} className={activeTab === section.key ? undefined : 'd-none'}>
                <MctGameTable
                  fetcher={section.fetcher}
                  columns={section.columns}
                  scopedColumns={section.scopedColumns}
                  noItemsLabel={section.noItemsLabel}
                  onOpenGame={setOpenGame}
                  onTotalChange={totalReporters[section.key]}
                />
              </div>
            ) : null,
          )}
        </CCardBody>
      </CCard>

      <MctGameDetailPanel game={openGame} visible={!!openGame} onClose={() => setOpenGame(null)} />
    </>
  )
}

export default MctGames
